import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import LoadingSpinner from 'components/LoadingSpinner';
import TitleBar from 'components/TitleBar';
import isNumber from 'lodash/isNumber';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';
import { useLocation } from 'react-router-dom';
import * as api from 'utils/apiCalls';
import InteractionsGraph from './graphs/InteractionsGraph';
import RosterGraph from './graphs/RosterGraph';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: '5em',
    paddingRight: '5em',
    paddingLeft: '5em',
    flexGrow: 1,
    textAlign: 'left',
  },
  header: {
    textAlign: 'center',
    paddingTop: theme.spacing(1),
  },
  button: {
    marginRight: theme.spacing(2),
  },
  main: {
    marginTop: theme.spacing(5),
  },
  graph: {
    height: '30vh',
    width: '100%',
    padding: theme.spacing(2),
  },
  headers: {
    fontSize: '15px',
    color: '#383838',
  },
  data: {
    fontSize: '3.5em',
    color: theme.palette.primary.main,
  },
  dataTitle: {
    fontSize: '.9em',
    color: theme.palette.primary.main,
    marginTop: '-10px',
  },
  icon: {
    fontSize: '.75em',
  },
}));

const LoadingStatus = {
  Finished: 'Finished',
  Loading: 'Loading',
  Failed: 'Failed',
};

const RosterAnalysis = () => {
  const classes = useStyles();
  const [attendees, setAttendees] = useState(0);
  const [meetingData, setMeetingData] = useState(0);
  const [meetingLength, setMeetingLength] = useState(null);
  const [externalMeeting, setExternalMeeting] = useState();
  const [instanceId, setInstanceId] = useState();
  const [instances, setInstances] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.Loading);
  const queryRef = new URLSearchParams(useLocation().search);
  const query = useRef(queryRef);
  query.current = queryRef;

  const timeDiffInMs = (start, finish) => {
    start = isNumber(start) ? start : parseInt(start);
    finish = isNumber(finish) ? finish : parseInt(finish);
    return moment(finish).diff(moment(start), 'milliseconds');
  };

  useEffect(() => {
    (async () => {
      const externalMeetingId = query.current.get('meetingId');
      const instanceId = query.current.get('instanceId');
      if (externalMeetingId && instanceId) {
        const meeting = await api.meetingInfo(externalMeetingId);
        const instances = await api.meetingInstances(externalMeetingId);
        const meetingArray = [];
        instances
          .sort((a, b) => {
            if (a.StartDate < b.StartDate) return 1;
            if (a.StartDate > b.StartDate) return -1;
            return 0;
          })
          .forEach((instance) => {
            meetingArray.push({
              label: moment(instance.StartDate).format('dddd, MMMM Do YYYY, h:mm a'),
              value: instance.ChimeMeetingId,
            });
          });

        setExternalMeeting(meeting);
        setInstanceId(instanceId);
        setInstances(meetingArray);
      }
    })();
  }, []);

  const updateGraph = async () => {
    const meetingData = await api.getMeetingAnalysis(externalMeeting?.MeetingId, instanceId);
    if (!meetingData.meeting || !meetingData.attendees) return;
    const totalMeetingTime = timeDiffInMs(
      meetingData.meeting.StartDate,
      meetingData.meeting.EndDate
    );
    setMeetingLength(new Date(totalMeetingTime).toISOString().slice(11, -5));
    setAttendees(meetingData.attendees.length);
    setMeetingData(meetingData);
    setLoadingStatus(LoadingStatus.Finished);
  };

  useEffect(() => {
    setLoadingStatus(LoadingStatus.Loading);
    updateGraph();
    //eslint-disable-next-line
  }, [instanceId]);

  return (
    <div>
      <TitleBar />
      <Box display="flex" flexDirection="row" justifyContent="center" className={classes.header}>
        <Typography variant="h4">{externalMeeting?.MeetingName}</Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        className={classes.header}
        style={{ paddingLeft: '5em' }}
      >
        <Dropdown
          value={instanceId}
          options={instances}
          onChange={async (instance) => {
            setInstanceId(instance.value);
          }}
          placeholder={'No meetings'}
        />
      </Box>
      {loadingStatus === LoadingStatus.Loading && <LoadingSpinner />}
      {loadingStatus === LoadingStatus.Failed && <>Error</>}
      {loadingStatus === LoadingStatus.Finished && (
        <>
          <Grid container className={classes.root}>
            <Grid item xs={12} container>
              <Grid item md={2} lg={3} implementation="css" smDown component={Hidden} />

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Grid className={classes.headers} item sm={12}>
                  Meeting
                </Grid>
                <Grid className={classes.center} item>
                  <div className={classes.data}>{meetingLength}</div>
                  <div className={classes.dataTitle}>Total Time</div>
                </Grid>

                <Grid className={classes.headers} style={{ marginTop: '1em' }} item sm={12}>
                  Attendees
                </Grid>
                <Grid className={classes.center} item>
                  <div className={classes.data}>{attendees}</div>
                  <div className={classes.dataTitle}>Total</div>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Grid item sm={12} container direction="column" spacing={1}>
                  <InteractionsGraph
                    externalMeetingId={query.current.get('meetingId')}
                    instanceId={instanceId}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <RosterGraph meetingData={meetingData} />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};
export default RosterAnalysis;
